import { motion } from 'framer-motion';
import { FaHeartbeat, FaCreditCard, FaTruck, FaGraduationCap, FaHome, FaShoppingBag, FaArrowRight } from 'react-icons/fa';
import BouncingDots from '../Common/BouncingDots';

import indHealthcare from '../../assets/ind_healthcare.png';
import indFintech from '../../assets/ind_fintech.png';
import indLogistics from '../../assets/ind_logistics.png';
import indEducation from '../../assets/ind_education.png';
import indRealestate from '../../assets/ind_realestate.png';
import indEcommerce from '../../assets/ind_ecommerce.png';

const industries = [
  {
    title: 'Healthcare',
    tag: 'HIPAA Compliant',
    description: 'Patient engagement platforms, telemedicine infrastructure, and compliant digital marketing for hospitals, clinics, and health-tech startups.',
    icon: FaHeartbeat,
    image: indHealthcare,
    span: 'lg:col-span-2',
    minH: 'min-h-[320px]',
  },
  {
    title: 'Fintech',
    tag: 'Regulatory-Ready',
    description: 'Secure payment gateways, KYC/AML-compliant product builds, and performance marketing for financial services.',
    icon: FaCreditCard,
    image: indFintech,
    span: 'lg:col-span-1',
    minH: 'min-h-[320px]',
  },
  {
    title: 'Logistics & Supply Chain',
    tag: 'Real-Time Systems',
    description: 'Fleet tracking dashboards, supply-chain visibility portals, and targeted B2B campaigns that reduce CAC across logistics verticals.',
    icon: FaTruck,
    image: indLogistics,
    span: 'lg:col-span-1',
    minH: 'min-h-[280px]',
  },
  {
    title: 'Education & EdTech',
    tag: 'LMS & Learner Engagement',
    description: 'E-learning platforms, cohort-based course infrastructure, and student acquisition funnels engineered for retention and lifetime value.',
    icon: FaGraduationCap,
    image: indEducation,
    span: 'lg:col-span-2',
    minH: 'min-h-[280px]',
  },
  {
    title: 'Real Estate',
    tag: 'Lead Generation Systems',
    description: 'Virtual tour platforms, CRM-integrated lead portals, and hyper-targeted geo campaigns for residential and commercial developers.',
    icon: FaHome,
    image: indRealestate,
    span: 'lg:col-span-2',
    minH: 'min-h-[300px]',
  },
  {
    title: 'E-commerce & Retail',
    tag: 'ROAS-Optimized',
    description: 'Headless commerce builds, inventory automation, and full-funnel performance marketing designed to maximize revenue-per-visitor.',
    icon: FaShoppingBag,
    image: indEcommerce,
    span: 'lg:col-span-1',
    minH: 'min-h-[300px]',
  },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const IndustryExpertiseSection = () => (
  <section className="w-full bg-[#FAFBF9] border-t border-[#F1F3F1] relative overflow-hidden">
    <BouncingDots />

    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-40 relative z-10">

      {/* Heading */}
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
        variants={fadeUp} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="mb-16 sm:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
      >
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
            ✦&nbsp;&nbsp;Vertical Specialization&nbsp;&nbsp;✦
          </span>
          <h2 className="leading-[1.05] tracking-tight">
            <span className="block text-3xl sm:text-4xl md:text-5xl font-normal text-black/25">
              Built for your
            </span>
            <span className="block text-4xl sm:text-5xl md:text-[64px] font-bold text-black">
              <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">industry. Not just any.</span>
            </span>
          </h2>
        </div>
        <p className="text-base text-[#5A635A] font-normal max-w-sm leading-relaxed lg:text-right">
          Sector-specific knowledge means faster launch cycles, fewer compliance surprises, and better outcomes.
        </p>
      </motion.div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {industries.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.75, delay: Math.min(i * 0.055, 0.22), ease: [0.16, 1, 0.3, 1] }}
              className={`relative overflow-hidden rounded-[24px] group cursor-pointer ${item.span} ${item.minH}`}
            >
              {/* Image */}
              <img
                src={item.image} alt={item.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy" decoding="async"
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/85 via-[#0a0a0a]/25 to-transparent transition-all duration-500" />
              <div className="absolute inset-0 bg-[#0a0a0a]/0 group-hover:bg-[#0a0a0a]/20 transition-colors duration-500" />

              {/* Tag (top-left) */}
              <div className="absolute top-5 left-5">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-[10px] font-semibold text-white/80 uppercase tracking-wider backdrop-blur-sm">
                  <Icon size={9} />
                  {item.tag}
                </span>
              </div>

              {/* Bottom content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-0 leading-tight">
                  {item.title}
                </h3>

                {/* Description slides up on hover */}
                <div className="max-h-0 overflow-hidden group-hover:max-h-32 transition-all duration-500 ease-out">
                  <p className="text-white/65 text-sm leading-relaxed mt-3 font-normal">
                    {item.description}
                  </p>
                  <div className="flex items-center gap-2 mt-4 text-[11px] font-semibold uppercase tracking-wider text-white/50 group-hover:text-white transition-colors duration-300">
                    <span>View Case Study</span>
                    <FaArrowRight size={9} />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom CTA line */}
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true }}
        variants={fadeUp} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="mt-12 sm:mt-16 flex items-center justify-between border-t border-[#EAECE9] pt-8"
      >
        <p className="text-sm text-[#5A635A] font-normal">
          Working in a niche not listed?
        </p>
        <a href="/contact"
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1C2E1E] hover:opacity-60 transition-opacity group">
          Let's talk specifics
          <FaArrowRight size={9} className="group-hover:translate-x-1 transition-transform" />
        </a>
      </motion.div>
    </div>
  </section>
);

export default IndustryExpertiseSection;
